import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from 'axios';

// Create axios instance with base configuration
const axiosInstance: AxiosInstance = axios.create({
  // Add timeout to prevent hanging requests
  timeout: 60000, // 60 seconds
  // Disable automatic retries to reduce OPTIONS requests
  validateStatus: (status) => status >= 200 && status < 300,
});

// Request interceptor for axios 1.x
// Uses InternalAxiosRequestConfig for axios 1.x compatibility
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Ensure headers exist
    config.headers = config.headers || {};

    // If data is FormData, remove Content-Type to let browser set it
    // Otherwise, ensure JSON content type is set if not already specified
    if (config.data instanceof FormData) {
      // Remove Content-Type for FormData - browser will set multipart/form-data with boundary
      delete config.headers['Content-Type'];
    } else if (!config.headers['Content-Type']) {
      // Set JSON content type only if not already set and not FormData
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - let Auth0 SDK handle authentication errors
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    // Better error handling for axios 1.x
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      return Promise.reject(error.response.data || 'Server error occurred');
    } else if (error.request) {
      // The request was made but no response was received
      return Promise.reject('No response from server');
    } else {
      // Something happened in setting up the request that triggered an Error
      return Promise.reject(error.message || 'Something went wrong');
    }
  }
);

export default axiosInstance;

// Export type helpers for axios 1.x
export type {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
};
